import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  TextField,
  Box,
  Typography,
  Container,
  Paper,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  CheckCircle,
  ErrorOutline,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Logo } from "../../../Components/Logo";
import { Api } from "../../../Utils/Api";
import { IUsuario, useUsuario } from "../../../Hooks/UsuarioHook";
import { Email, WhatsApp } from "@mui/icons-material";
import { app_base_url } from "../../../Utils/Urls";

interface RecuperarSenha {
  id: number;
  id_usuario: number;
  link: string;
  codigo: string;
  criacao: string;
  ativo: boolean;
}

export const RedefinirSenha = () => {
  const { logar } = useUsuario();

  const [searchParams] = useSearchParams();
  const link = searchParams.get("link") || "";

  const [code, setCode] = useState(["", "", "", "", ""]);
  const [chave, setChave] = useState<RecuperarSenha | null>(null);
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [timeLeft, setTimeLeft] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"codigo" | "senha">("codigo");
  const [usuario, setUsuario] = useState({} as IUsuario);

  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((prev) => !prev);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    setPasswordError(e.target.value.length < 4);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordError(
      e.target.value.length < 4 || e.target.value !== newPassword
    );
  };

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  async function redefineSenha() {
    const { data } = await Api.post<{
      mensagem: string;
      status: boolean;
      data: { id: number; senha: string };
    }>(`/redefinir-senha`, {
      id_usuario: usuario.usuario.id,
      senha: newPassword,
      link: chave?.link,
      codigo: chave?.codigo,
    });

    if (data.status) {
      logar(data.data.id.toString(), data.data.senha, "id");
    } else {
      window.alert(data.mensagem);
    }
  }

  // Busca os dados da chave de recuperação
  useEffect(() => {
    const fetchChave = async () => {
      if (link) {
        try {
          const { data } = await Api.get(`/recupera-senha/${link}`);
          if (data.status) {
            setChave(data.data);
          }
        } catch (error) {
          console.error("Erro ao buscar chave de recuperação:", error);
        }
      }
    };

    fetchChave();
  }, [link]);

  // Calcula o tempo restante para expiração do código
  useEffect(() => {
    if (chave?.ativo == false) {
      setIsExpired(true);
    }

    if (chave?.criacao) {
      const expirationTime = new Date(chave.criacao);
      expirationTime.setHours(expirationTime.getHours() + 24);

      const interval = setInterval(() => {
        const now = new Date();
        const timeDiff = expirationTime.getTime() - now.getTime();

        if (timeDiff <= 0) {
          setTimeLeft("Código expirado");
          setIsExpired(true);
          clearInterval(interval);
        } else {
          const hours = Math.floor(timeDiff / (1000 * 60 * 60));
          const minutes = Math.floor(
            (timeDiff % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
          setTimeLeft(`O código expirará em ${hours}h ${minutes}m ${seconds}s`);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [chave?.criacao]);

  useEffect(() => {
    const fetchData = async () => {
      if (chave?.id_usuario) {
        try {
          const { data } = await Api.get(`/por-id/${chave.id_usuario}`);
          const help = {} as IUsuario;
          help.usuario = data.data;
          setUsuario(help); // Supondo que você tenha um estado setUsuario
        } catch (error) {
          console.error("Erro ao buscar usuário:", error);
        }
      }
    };

    fetchData();
  }, [chave]);

  const handleKeyDown = (index: number, event: any) => {
    if (event.key === "Backspace") {
      event.preventDefault(); // Impede o comportamento padrão

      const newCode = [...code];
      newCode[index] = ""; // Apaga o valor atual
      setCode(newCode);

      // Move o cursor para a esquerda, se não estiver no primeiro campo
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Permite apenas números

    let newCode = [...code];

    // Divide os números digitados em um array
    const digits = value.split("").slice(0, 5 - index); // Limita para evitar overflow

    digits.forEach((digit, i) => {
      const pos = index + i;
      if (pos < 5) {
        newCode[pos] = digit;
      }
    });

    setCode(newCode);

    // Move o cursor para o próximo campo disponível
    const nextIndex = index + digits.length;
    if (nextIndex < 5) {
      inputRefs.current[nextIndex]?.focus();
    }

    const enteredCode = newCode.join("");

    if (enteredCode.length === 5) {
      if (chave && enteredCode === chave.codigo) {
        setIsCodeValid(true);
        setShowSuccessMessage(true);
        setIsLoading(true);

        setTimeout(() => {
          setShowSuccessMessage(false);
          setIsLoading(false);
          setStep("senha"); // Agora mostramos os campos de redefinição de senha
        }, 1500);
      } else {
        setIsCodeValid(false);

        // Apaga os campos e volta o cursor para o primeiro input
        setTimeout(() => {
          setCode(["", "", "", "", ""]);
          inputRefs.current[0]?.focus();
        }, 500);
      }
    } else {
      setIsCodeValid(null);
    }
  };

  // Cola o código nos campos automaticamente
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault(); // Impede a inserção direta no input

    const pastedData = event.clipboardData.getData("text").trim();

    // Verifica se os dados colados são exatamente 5 dígitos numéricos
    if (/^\d{5}$/.test(pastedData)) {
      setCode(pastedData.split(""));
      setIsCodeValid(pastedData === chave?.codigo);
      setShowSuccessMessage(pastedData === chave?.codigo);
      setIsLoading(pastedData === chave?.codigo);

      if (pastedData === chave?.codigo) {
        setTimeout(() => {
          setShowSuccessMessage(false);
          setIsLoading(false);
          setStep("senha"); // Agora mostramos os campos de redefinição de senha
        }, 1500);
      } else {
        setTimeout(() => {
          setCode(["", "", "", "", ""]);
          inputRefs.current[0]?.focus();
          setIsCodeValid(false);
        }, 500);
      }
    } else {
      // Se o código colado for inválido, apaga os campos e foca no primeiro input
      setCode(["", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      setIsCodeValid(false);
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: 3, width: "90%", textAlign: "center", borderRadius: 2 }}
      >
        <Box mb={2}>
          <Logo />
        </Box>

        <Typography variant="h5" fontWeight="bold" color="primary" mb={1}>
          Redefinir Senha
        </Typography>

        {isExpired ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            mt={3}
            p={3}
            sx={{
              backgroundColor: "rgba(211,47,47,0.12)",
              borderRadius: 2,
              border: "1px solid rgba(211,47,47,0.5)",
              boxShadow: "0 4px 10px rgba(211, 47, 47, 0.2)",
              maxWidth: "400px",
              mx: "auto",
            }}
          >
            {/* Ícone de alerta grande */}

            <Typography variant="h6" color="error" fontWeight="bold">
              ⚠️
            </Typography>

            {/* Mensagem de erro destacada */}
            <Typography variant="h6" color="error" fontWeight="bold" mt={1}>
              O link de recuperação expirou!
            </Typography>

            <Typography variant="body1" color="error" mt={1}>
              Este link não é mais válido. Para redefinir sua senha, clique no
              botão abaixo e gere um novo link.
            </Typography>

            {/* Botão para gerar um novo link */}
            <Button
              component={Link}
              to={`${app_base_url}/recuperar-senha`}
              variant="contained"
              color="error"
              sx={{
                mt: 3,
                py: 1.2,
                px: 3,
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              🔄 Gerar Novo Link
            </Button>
          </Box>
        ) : (
          <>
            {step === "codigo" && (
              <>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  fontWeight="bold"
                  gutterBottom
                >
                  👋 Olá, <strong>{usuario?.usuario?.nome}</strong>!
                </Typography>

                <Typography variant="body1" color="textSecondary">
                  Para recuperar o acesso à sua conta, insira o código de{" "}
                  <strong>5 dígitos</strong> que enviamos para você via
                  <Email
                    fontSize="small"
                    sx={{ verticalAlign: "middle", mx: 0.5 }}
                  />{" "}
                  e-mail ou
                  <WhatsApp
                    fontSize="small"
                    sx={{ verticalAlign: "middle", mx: 0.5, color: "green" }}
                  />{" "}
                  WhatsApp.
                </Typography>

                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                  mt={2}
                >
                  🔐 Digite o código recebido:
                </Typography>

                <Box display="flex" justifyContent="center" gap={1} mt={2}>
                  {code.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      variant="outlined"
                      error={isCodeValid === false}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          fontSize: "22px",
                          width: "50px",
                          height: "55px",
                          textAlign: "center",
                        },
                      }}
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: "center", fontSize: "24px" },
                      }}
                    />
                  ))}
                </Box>

                {isCodeValid === false && (
                  <Typography variant="body2" color="error" mt={2}>
                    Código incorreto. Verifique e tente novamente.
                  </Typography>
                )}
              </>
            )}

            {showSuccessMessage && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                mt={2}
                color="success"
              >
                <CheckCircle fontSize="large" sx={{ color: "green" }} />
                <Typography
                  variant="body1"
                  sx={{ color: "green", fontWeight: "bold" }}
                >
                  Código correto! Redirecionando...
                </Typography>
              </Box>
            )}

            {isLoading && <CircularProgress sx={{ mt: 2 }} />}

            {step === "senha" && (
              <Box mt={2}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  textAlign="center"
                >
                  ✅ Código verificado com sucesso! Agora, redefina sua senha e
                  confirme-a para recuperar o acesso à sua conta.
                </Typography>

                {/* Campo Nova Senha */}
                <Typography variant="body1" color="textSecondary" mt={2}>
                  🔒 Digite sua nova senha:
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={newPassword}
                  onChange={handlePasswordChange}
                  error={passwordError}
                  helperText={
                    passwordError
                      ? "A senha deve ter pelo menos 4 caracteres."
                      : ""
                  }
                  sx={{ mt: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Campo Confirmação de Senha */}
                <Typography variant="body1" color="textSecondary" mt={2}>
                  🔁 Confirme sua nova senha:
                </Typography>
                <TextField
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={confirmPasswordError}
                  helperText={
                    confirmPasswordError
                      ? confirmPassword.length < 4
                        ? "A senha deve ter pelo menos 4 caracteres."
                        : "As senhas não coincidem."
                      : ""
                  }
                  sx={{ mt: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Botão de Confirmação */}
                {/* Lógica para verificar se o botão deve estar desabilitado */}

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<CheckCircle />}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    fontSize: "16px",
                    fontWeight: "bold",
                    borderRadius: 2,
                  }}
                  onClick={redefineSenha}
                  disabled={
                    passwordError ||
                    confirmPasswordError ||
                    newPassword.length < 4 ||
                    confirmPassword !== newPassword
                  } // ✅ Botão só fica habilitado se a senha for válida!
                >
                  Confirmar Senha
                </Button>
              </Box>
            )}
          </>
        )}

        {timeLeft && !isExpired && (
          <Typography variant="body2" color="error" mt={2}>
            {timeLeft}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};
